'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, RefreshCw, BookOpen, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Western Fantasy Stories
const westernFantasyStories = [
  {
    title: "Ella and the Clockwork Forest",
    story: "In a sleepy English village, Ella finds a key that awakens a forest where trees tick like clocks—she must reset the Heartgear Tree before time runs out.",
    theme: "Time & Magic"
  },
  {
    title: "Jack and the Song of the Highlands",
    story: "In the Scottish Highlands, Jack discovers a haunted bagpipe that plays melodies summoning ancient guardians to defend the land from a shadow mist.",
    theme: "Music & Courage"
  },
  {
    title: "Lina and the Viking Whisper",
    story: "In Norway, Lina hears whispers in the fjord winds and follows them to a buried longship where she must awaken a sleeping sea dragon to calm a brewing storm.",
    theme: "Adventure & Dragons"
  },
  {
    title: "Oliver and the Tower of Spells",
    story: "In London, Oliver finds a hidden door in Big Ben leading to a tower filled with forgotten spells—he must restore their magic before the city loses its charm.",
    theme: "Magic & Mystery"
  },
  {
    title: "Sophie and the Rose Garden Maze",
    story: "At Versailles, Sophie enters a rose maze that shifts with every step—she must find the Golden Rose to free a trapped fairy queen and her court.",
    theme: "Puzzles & Fairies"
  },
  {
    title: "Max and the Armor of Arthur",
    story: "At Camelot, Max discovers he can awaken the Knights of the Round Table by finding Arthur's lost armor pieces scattered across enchanted Britain.",
    theme: "Knights & Quests"
  },
  {
    title: "Emma and the Venetian Mask",
    story: "In Venice, Emma finds a mask that reveals hidden gondola paths leading to an underwater palace where she must solve riddles to save the sinking city.",
    theme: "Mystery & Water Magic"
  },
  {
    title: "Henry and the Berlin Wall of Wishes",
    story: "In Berlin, Henry discovers that remnants of the old wall grant wishes—but he must unite East and West spirits before the magic fades at dawn.",
    theme: "Unity & Wishes"
  },
  {
    title: "Isabella and the Flamenco Phoenix",
    story: "In Seville, Isabella's flamenco dancing awakens a phoenix whose feathers hold the power to paint the sky—she must perform the Dance of Dawn to restore color to Spain.",
    theme: "Dance & Rebirth"
  },
  {
    title: "Felix and the Swiss Mountain Echo",
    story: "In the Swiss Alps, Felix discovers his yodel creates avalanches of star-snow—he must sing the ancient mountain song to wake the sleeping giants who guard the peaks.",
    theme: "Music & Mountains"
  }
];

// Multicultural Fantasy Stories
const multiculturalFantasyStories = [
  {
    title: "Diya and the Arrow of Arjuna",
    story: "Diya is transported to the battlefield of Kurukshetra where she must deliver a divine arrow to Arjuna before the great war begins.",
    theme: "Destiny & Courage",
    culture: "Indian"
  },
  {
    title: "Kofi and the Drums of Destiny",
    story: "In Ghana, Kofi's djembe drum opens portals to the spirit realm where he must learn the rhythms of his ancestors to heal his village.",
    theme: "Music & Heritage",
    culture: "African"
  },
  {
    title: "Mei-Ling and the Dragon's Pearl",
    story: "Mei-Ling must return a stolen pearl to the Dragon King before the rivers dry up, journeying through magical Chinese landscapes.",
    theme: "Honor & Nature",
    culture: "Chinese"
  },
  {
    title: "Amara and the Sphinx's Riddle",
    story: "In ancient Egypt, Amara must solve three riddles to awaken the Sphinx and restore the flooding of the Nile.",
    theme: "Wisdom & Mystery",
    culture: "Egyptian"
  },
  {
    title: "Teo and the Jaguar Spirit",
    story: "In the Amazon, Teo bonds with a jaguar spirit to protect the rainforest from shadow creatures draining its life force.",
    theme: "Nature & Protection",
    culture: "Amazonian"
  },
  {
    title: "Kenji and the Origami Phoenix",
    story: "Kenji's paper crane comes alive and leads him through Japanese folklore to gather seven virtues before the cherry blossoms fall.",
    theme: "Art & Virtue",
    culture: "Japanese"
  },
  {
    title: "Aaliyah and the Desert Rose",
    story: "In the Arabian desert, Aaliyah must find the legendary Desert Rose that blooms once a century to save her oasis from eternal drought.",
    theme: "Perseverance & Magic",
    culture: "Arabian"
  },
  {
    title: "Mikhail and the Firebird's Feather",
    story: "Mikhail must capture a feather from the Firebird to light the eternal flame and end the endless winter covering Russia.",
    theme: "Bravery & Light",
    culture: "Russian"
  },
  {
    title: "Lupita and the Aztec Calendar",
    story: "Lupita discovers she can enter the Aztec calendar stone and must align the cosmic wheels to prevent the fifth sun from ending.",
    theme: "Time & Balance",
    culture: "Mexican"
  },
  {
    title: "Nia and the Baobab Portal",
    story: "Through a hollow baobab tree, Nia enters the realm where African folktales live and must unite Anansi and the Lion King.",
    theme: "Stories & Unity",
    culture: "African"
  },
  {
    title: "Raj and the Monsoon Palace",
    story: "Raj must restore the magical balance of the Monsoon Palace to bring back the rains and save his drought-stricken village.",
    theme: "Weather & Responsibility",
    culture: "Indian"
  },
  {
    title: "Li Wei and the Terracotta Warriors",
    story: "Li Wei accidentally awakens the Terracotta Army and must lead them in defending against shadow invaders from another realm.",
    theme: "Leadership & History",
    culture: "Chinese"
  },
  {
    title: "Fatima and the Flying Carpet",
    story: "Fatima's grandmother's carpet reveals its magic, taking her on a journey to collect moonlight threads to weave peace between warring djinn.",
    theme: "Peace & Magic",
    culture: "Arabian"
  },
  {
    title: "Akiko and the Kitsune's Gift",
    story: "Akiko befriends a fox spirit who teaches her to shape-shift, but she must master nine transformations to save the spirit realm.",
    theme: "Friendship & Growth",
    culture: "Japanese"
  },
  {
    title: "Diego and the Condor's Flight",
    story: "In the Andes, Diego gains the ability to fly with condors and must deliver a message to the Sky People before the mountain spirits awaken.",
    theme: "Freedom & Duty",
    culture: "Andean"
  }
];

interface StoryInspirationProps {
  onSelectTitle?: (title: string) => void;
  onSelectStory?: (story: string) => void;
  onSelectBoth?: (title: string, story: string) => void;
  variant?: 'compact' | 'full';
  showCulturalStories?: boolean;
}

export default function StoryInspiration({ 
  onSelectTitle, 
  onSelectStory, 
  onSelectBoth,
  variant = 'compact',
  showCulturalStories = true
}: StoryInspirationProps) {
  const [currentStory, setCurrentStory] = useState(0);
  const [storySet, setStorySet] = useState<'western' | 'multicultural'>('western');
  const [isAnimating, setIsAnimating] = useState(false);

  const stories = storySet === 'western' ? westernFantasyStories : multiculturalFantasyStories;

  const nextStory = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStory((prev) => (prev + 1) % stories.length);
      setIsAnimating(false);
    }, 150);
  };

  const handleSelectTitle = () => {
    const story = stories[currentStory];
    if (onSelectTitle) {
      // Replace the name with [Child's Name] for the title
      const adaptedTitle = story.title.replace(/^(\w+)/, "[Child's Name]");
      onSelectTitle(adaptedTitle);
    }
  };

  const handleSelectStory = () => {
    const story = stories[currentStory];
    if (onSelectStory) {
      // Adapt the story to be more generic
      const adaptedStory = story.story
        .replace(/^(\w+)/, "[Child's name]")
        .replace(/\b(he|she)\b/gi, "they")
        .replace(/\b(his|her)\b/gi, "their");
      onSelectStory(adaptedStory);
    }
  };

  const handleSelectBoth = () => {
    const story = stories[currentStory];
    if (onSelectBoth) {
      const adaptedTitle = story.title.replace(/^(\w+)/, "[Child's Name]");
      const adaptedStory = story.story
        .replace(/^(\w+)/, "[Child's name]")
        .replace(/\b(he|she)\b/gi, "they")
        .replace(/\b(his|her)\b/gi, "their");
      onSelectBoth(adaptedTitle, adaptedStory);
    }
  };

  if (variant === 'compact') {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Need Inspiration?
          </h4>
          <div className="flex gap-2">
            {/* I'll Make My Own button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                // Clear the fields to let user create their own
                if (onSelectBoth) {
                  onSelectBoth('', '');
                }
              }}
              className="text-xs"
            >
              I'll Make My Own
            </Button>
            {showCulturalStories && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setStorySet(storySet === 'western' ? 'multicultural' : 'western')}
                className="text-xs"
              >
                {storySet === 'western' ? 'Show World Stories' : 'Show Classic Stories'}
              </Button>
            )}
          </div>
        </div>
        
        <Card className={`border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 transition-all duration-300 ${isAnimating ? 'scale-95 opacity-50' : ''}`}>
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h5 className="font-semibold text-purple-900 text-sm">
                  {stories[currentStory].title}
                </h5>
                <Badge variant="secondary" className="text-xs">
                  {storySet === 'multicultural' 
                    ? (stories[currentStory] as any).culture 
                    : (stories[currentStory] as any).theme}
                </Badge>
              </div>
              <p className="text-xs text-gray-700 leading-relaxed">
                {stories[currentStory].story}
              </p>
              
              <div className="flex items-center justify-between pt-2">
                <div className="flex gap-2">
                  {onSelectTitle && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleSelectTitle}
                      className="text-xs"
                    >
                      Use Title
                    </Button>
                  )}
                  {onSelectStory && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleSelectStory}
                      className="text-xs"
                    >
                      Use Story
                    </Button>
                  )}
                  {onSelectBoth && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleSelectBoth}
                      className="text-xs"
                    >
                      Use Both
                    </Button>
                  )}
                </div>
                
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={nextStory}
                  className="text-xs"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <p className="text-xs text-gray-500 italic flex items-center gap-1">
          <BookOpen className="h-3 w-3" />
          {currentStory + 1} of {stories.length} magical story ideas
        </p>
      </div>
    );
  }

  // Full variant with grid view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          Story Inspiration Gallery
        </h3>
        <div className="flex gap-2">
          {/* I'll Make My Own button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (onSelectBoth) {
                onSelectBoth('', '');
              }
            }}
          >
            I'll Make My Own
          </Button>
          {showCulturalStories && (
            <>
              <Button
                type="button"
                variant={storySet === 'western' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStorySet('western')}
              >
                Classic Fantasy
              </Button>
              <Button
                type="button"
                variant={storySet === 'multicultural' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStorySet('multicultural')}
              >
                World Stories
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto p-1">
        {stories.map((story, index) => (
          <Card 
            key={index}
            className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
            onClick={() => {
              if (onSelectBoth) {
                const adaptedTitle = story.title.replace(/^(\w+)/, "[Child's Name]");
                const adaptedStory = story.story
                  .replace(/^(\w+)/, "[Child's name]")
                  .replace(/\b(he|she)\b/gi, "they")
                  .replace(/\b(his|her)\b/gi, "their");
                onSelectBoth(adaptedTitle, adaptedStory);
              }
            }}
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-sm line-clamp-1">{story.title}</h4>
                <Badge variant="secondary" className="text-xs shrink-0">
                  {storySet === 'multicultural' 
                    ? (story as any).culture 
                    : (story as any).theme}
                </Badge>
              </div>
              <p className="text-xs text-gray-600 line-clamp-3">{story.story}</p>
              <div className="pt-2 flex items-center text-xs text-purple-600">
                <span>Click to use</span>
                <ChevronRight className="h-3 w-3 ml-1" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
